import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import "core-js/modules/es.array.filter.js";
import "core-js/modules/es.array.iterator.js";
import "core-js/modules/es.array.map.js";
import "core-js/modules/es.object.to-string.js";
import "core-js/modules/es.promise.js";
import "core-js/modules/es.string.iterator.js";
import "core-js/modules/web.dom-collections.iterator.js";
import { scrollToElementAfterTimeout } from "@faire/help-center-utils/lib/window";
import { useStrictLocalization } from "@faire/web/common/localization";
import { isNotUndefined } from "@faire/web/common/typescriptUtils";
import { useState } from "@faire/web/ui/hooks/useState";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import React, { useEffect, useLayoutEffect, useRef } from "react";
import { useAsyncDataStatus } from "../hooks/useAsyncDataStatus";
import { useNodesConditionalVisibilityState } from "../hooks/useNodesConditionalVisibilityState";
import { usePeteEventAdditionalDataEffect } from "../hooks/usePeteEventAdditionalDataEffect";
import { getDefaultOtherNodeOrUndefined } from "../utils/getDefaultOtherNodeOrUndefined";
import { getNodesThatCanBeRendered } from "../utils/getNodesThatCanBeRendered";
import { CenteredLoadingSpinner } from "./CenteredLoadingSpinner";
import { IssueTopicCard } from "./IssueTopicCard/IssueTopicCard";
import { OtherIssueTopicCard } from "./OtherIssueTopicCard";
var ONE_HUNDRED_MILLISECONDS = 100;
/**
 * General purpose component to render the children of a node
 */
export var NodeChildrenRenderer = function NodeChildrenRenderer(_ref) {
  var _node$children;
  var context = _ref.context,
    node = _ref.node,
    selectedNode = _ref.selectedNode,
    onClickOnNode = _ref.onClickOnNode,
    isAutofocusOnMobile = _ref.isAutofocusOnMobile,
    bottomElementRef = _ref.bottomElementRef;
  var _useViewport = useViewport(),
    isMobile = _useViewport.isMobile;
  var _useStrictLocalizatio = useStrictLocalization(),
    strictLocalize = _useStrictLocalizatio.strictLocalize;
  var _useAsyncDataStatus = useAsyncDataStatus(),
    asyncDataInProgress = _useAsyncDataStatus.asyncDataInProgress,
    asyncDataComplete = _useAsyncDataStatus.asyncDataComplete,
    setAsInProgress = _useAsyncDataStatus.setAsInProgress,
    setAsComplete = _useAsyncDataStatus.setAsComplete,
    setAsNotRequestedYet = _useAsyncDataStatus.setAsNotRequestedYet;
  var _useNodesConditionalV = useNodesConditionalVisibilityState(),
    nodesConditionalVisibility = _useNodesConditionalV.nodesConditionalVisibility,
    updateNodeVisibility = _useNodesConditionalV.updateNodeVisibility;
  usePeteEventAdditionalDataEffect(context, selectedNode);
  useEffect(function () {
    setAsNotRequestedYet();
  }, [node, setAsNotRequestedYet]);
  useEffect(function () {
    if (node && node.children && !asyncDataInProgress && !asyncDataComplete) {
      setAsInProgress();
      callVisibilityFunctions(context, node.children, updateNodeVisibility).then(setAsComplete);
    }
  }, [node, context, asyncDataInProgress, asyncDataComplete, setAsComplete, setAsInProgress, updateNodeVisibility]);
  var nodesThatShouldBeRendered = getNodesThatCanBeRendered(context.tree, (_node$children = node === null || node === void 0 ? void 0 : node.children) !== null && _node$children !== void 0 ? _node$children : []).filter(function (node) {
    return filterOutNodesThatAreNotVisible(node, nodesConditionalVisibility);
  });
  var issueTopicCardRefs = useRef(nodesThatShouldBeRendered.map(function () {
    return /*#__PURE__*/React.createRef();
  }));
  var _useState = useState(94),
    _useState2 = _slicedToArray(_useState, 2),
    maxHeight = _useState2[0],
    setMaxHeight = _useState2[1];
  useLayoutEffect(function () {
    var heights = issueTopicCardRefs.current.map(function (ref) {
      var _ref$current$offsetHe, _ref$current;
      return (_ref$current$offsetHe = (_ref$current = ref.current) === null || _ref$current === void 0 ? void 0 : _ref$current.offsetHeight) !== null && _ref$current$offsetHe !== void 0 ? _ref$current$offsetHe : 0;
    });
    var maxHeight = Math.max.apply(Math, _toConsumableArray(heights));
    setMaxHeight(maxHeight);
  }, [nodesThatShouldBeRendered]);
  if (!node || !node.children) {
    return null;
  }
  if (!asyncDataComplete) {
    return /*#__PURE__*/React.createElement(CenteredLoadingSpinner, null);
  }
  var handleNodeClick = /*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(node) {
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            onClickOnNode(node);
            // Assign setting for experiment when nodes are clicked
            if (!node.assignSetting) {
              _context.next = 4;
              break;
            }
            _context.next = 4;
            return node.assignSetting();
          case 4:
            if (isAutofocusOnMobile && isMobile) {
              scrollToElementAfterTimeout(bottomElementRef === null || bottomElementRef === void 0 ? void 0 : bottomElementRef.current, ONE_HUNDRED_MILLISECONDS);
            }
          case 5:
          case "end":
            return _context.stop();
        }
      }, _callee);
    }));
    return function handleNodeClick(_x) {
      return _ref2.apply(this, arguments);
    };
  }();
  var otherNode = getDefaultOtherNodeOrUndefined(nodesThatShouldBeRendered, strictLocalize);
  return /*#__PURE__*/React.createElement(React.Fragment, null, nodesThatShouldBeRendered.map(function (node, index) {
    return /*#__PURE__*/React.createElement(IssueTopicCard, {
      key: node.id,
      text: node.label,
      icon: node.icon,
      isSelected: node.id === (selectedNode === null || selectedNode === void 0 ? void 0 : selectedNode.id),
      onClick: function onClick() {
        return handleNodeClick(node);
      },
      dataTestId: "self-serve-node-".concat(node.id),
      buttonRef: issueTopicCardRefs.current[index],
      style: {
        minHeight: isMobile ? "".concat(maxHeight, "px") : "auto"
      }
    });
  }), /*#__PURE__*/React.createElement(OtherIssueTopicCard, {
    otherNode: otherNode,
    selectedNode: selectedNode,
    parentNode: node,
    onClick: handleNodeClick,
    style: {
      minHeight: isMobile ? "".concat(maxHeight, "px") : "auto"
    }
  }));
};
var callVisibilityFunctions = function callVisibilityFunctions(context, children, updateNodeVisibility) {
  var visibilityPromises = getNodesThatCanBeRendered(context.tree, children).filter(function (node) {
    return isNotUndefined(node.isNodeVisible);
  }).map(function (node) {
    return node.isNodeVisible(context).then(function (isVisible) {
      updateNodeVisibility(node, isVisible);
      return isVisible;
    });
  });
  return Promise.all(visibilityPromises);
};
/**
 * Filters out nodes based on the nodeVisibility state property value for the node
 */
export var filterOutNodesThatAreNotVisible = function filterOutNodesThatAreNotVisible(node, nodesConditionalVisibility) {
  var nodeVisibility = nodesConditionalVisibility[node.id];
  var nodeIsVisibleByDefault = nodeVisibility === undefined;
  var nodeIsVisibleByCalculation = nodeVisibility === true;
  return nodeIsVisibleByDefault || nodeIsVisibleByCalculation;
};